import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMyClockStatus, updateMyLocation } from '../lib/api';

// This component runs in the background for employees
// It checks if they're clocked in and sends GPS updates
export const GpsTracker = () => {
    const { user, isAdmin } = useAuth();
    const intervalRef = useRef(null);
    const watchIdRef = useRef(null);
    const isTrackingRef = useRef(false);

    const startTracking = useCallback(() => {
        if (isTrackingRef.current) {
            return;
        }

        if (!navigator.geolocation) {
            return;
        }

        isTrackingRef.current = true;

        // Start watching position
        watchIdRef.current = navigator.geolocation.watchPosition(
            async (position) => {
                try {
                    await updateMyLocation(
                        position.coords.latitude,
                        position.coords.longitude,
                        position.coords.accuracy
                    );
                } catch {
                    // Silent GPS send error
                }
            },
            () => {
                // Silent GPS error - user may have denied permission
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 30000
            }
        );
    }, []);

    const stopTracking = useCallback(() => {
        if (!isTrackingRef.current) {
            return;
        }
        isTrackingRef.current = false;
        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
    }, []);

    useEffect(() => {
        // Only track for employees, not admins or terminal
        if (!user) {
            return;
        }
        
        if (isAdmin()) {
            return;
        }
        
        if (user.role === 'TERMINAL') {
            return;
        }


        // Check clock status and start tracking if clocked in
        const checkAndTrack = async () => {
            try {
                const res = await getMyClockStatus();
                
                if (res.data.is_clocked_in) {
                    startTracking();
                } else {
                    stopTracking();
                }
            } catch {
                // Silent clock status check error
            }
        };

        // Check immediately and then every 30 seconds
        checkAndTrack();
        intervalRef.current = setInterval(checkAndTrack, 30000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            stopTracking();
        };
    }, [user, isAdmin, startTracking, stopTracking]);

    // This component renders nothing - it just runs in background
    return null;
};

export default GpsTracker;
